import { useState, useEffect, useCallback } from 'react'
import { useApi } from '@/hooks/useApi'
import { Badge } from '@/components/ui'
import {
  IconCalendar, IconPlay, IconFileText, IconRefresh, IconExternalLink, IconMessageSquare,
} from '@/components/icons/Icons'
import type { BadgeVariant } from '@/components/ui'

/*
  Las reuniones del proyecto, con lo que Meet dejó de cada una.

  Los registros son ENLACES a Drive, no archivos copiados: una grabación pesa
  cientos de megas y ya está pagada ahí. Por eso abren en una pestaña nueva en
  vez de descargarse desde el ERP.

  El vínculo con el proyecto no es directo — va por el lead — pero eso lo
  resuelve el backend en `by-proyecto`, así que acá no hace falta saberlo.
*/
interface Registro {
  tipo: 'grabacion' | 'notas' | 'transcripcion' | 'otro'
  nombre: string
  url: string
  drive_id?: string
  mime?: string
  size_bytes?: number
}

interface Reunion {
  id: number
  fecha: string
  hora_inicio: string
  hora_fin: string
  nombre: string
  email: string
  estado: string
  google_meet_url: string | null
  notas: string | null
  registros: Registro[] | null
  registros_sync_at: string | null
}

const ESTADO: Record<string, { label: string; variant: BadgeVariant }> = {
  confirmado: { label: 'Confirmada', variant: 'enviada' },
  completado: { label: 'Realizada', variant: 'completado' },
  cancelado: { label: 'Cancelada', variant: 'pausado' },
  no_show: { label: 'No asistió', variant: 'pausado' },
}

const ETIQUETA_REGISTRO: Record<Registro['tipo'], string> = {
  grabacion: 'Grabación',
  notas: 'Notas',
  transcripcion: 'Transcripción',
  otro: 'Archivo',
}

function IconoRegistro({ tipo }: { tipo: Registro['tipo'] }) {
  if (tipo === 'grabacion') return <IconPlay size={14} />
  if (tipo === 'transcripcion') return <IconMessageSquare size={14} />
  return <IconFileText size={14} />
}

function pesoLegible(bytes?: number) {
  // Por debajo de 1 MB no se muestra: un Google Doc pesa unos KB y «0 MB» al
  // lado de «Notas» no informa nada. El peso importa para la grabación.
  if (!bytes || bytes < 1048576) return null
  const mb = bytes / 1048576
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${Math.round(mb)} MB`
}

function fechaLarga(fecha: string) {
  const d = new Date(fecha + 'T12:00:00')
  return d.toLocaleDateString('es-PE', { day: 'numeric', month: 'long', year: 'numeric' })
}

export default function ReunionesProyecto({ proyectoId }: { proyectoId: number }) {
  const api = useApi()
  const [reuniones, setReuniones] = useState<Reunion[]>([])
  const [loading, setLoading] = useState(true)
  const [sincronizando, setSincronizando] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  // `api` fuera de las dependencias: useApi() devuelve un objeto nuevo en cada
  // render y dejaría el efecto recargando en bucle.
  const cargar = useCallback(async () => {
    setLoading(true)
    try {
      setReuniones(await api.get<Reunion[]>(`/api/booking/agendamientos/by-proyecto/${proyectoId}`))
    } catch (err) {
      console.error('Error cargando reuniones:', err)
    } finally {
      setLoading(false)
    }
  }, [proyectoId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { cargar() }, [cargar])

  async function sincronizar(id: number) {
    setSincronizando(id)
    setError(null)
    try {
      await api.post(`/api/booking/agendamientos/${id}/registros/sincronizar`, {})
      await cargar()
    } catch (e: any) {
      setError(e?.message || 'No se pudo buscar en Drive.')
    } finally {
      setSincronizando(null)
    }
  }

  if (loading) return null
  if (reuniones.length === 0) return null

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <IconCalendar size={16} />
        <h3 style={{ margin: 0 }}>Reuniones</h3>
        <span className="text-muted" style={{ marginLeft: 'auto', fontSize: 14 }}>
          {reuniones.length} {reuniones.length === 1 ? 'reunión' : 'reuniones'}
        </span>
      </div>

      {error && (
        <p style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>{error}</p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {reuniones.map((r, i) => {
          const badge = ESTADO[r.estado] ?? { label: r.estado, variant: 'pausado' as BadgeVariant }
          const registros = r.registros || []
          return (
            <div
              key={r.id}
              style={{
                paddingBottom: 14,
                borderBottom: i < reuniones.length - 1 ? '1px solid var(--border)' : 'none',
              }}
            >
              <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600 }}>{fechaLarga(r.fecha)}</span>
                <span className="text-muted" style={{ fontSize: 14 }}>
                  {r.hora_inicio}–{r.hora_fin} · {r.nombre}
                </span>
                <Badge variant={badge.variant}>{badge.label}</Badge>
                <button
                  className="btn btn-sm btn-secondary"
                  style={{ marginLeft: 'auto' }}
                  onClick={() => sincronizar(r.id)}
                  disabled={sincronizando === r.id}
                  title="Buscar la grabación y las notas en Drive"
                >
                  <IconRefresh size={13} />
                  {sincronizando === r.id ? ' Buscando…' : ' Buscar en Drive'}
                </button>
              </div>

              {registros.length > 0 ? (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  {registros.map((g, k) => (
                    <a
                      key={k}
                      className="btn btn-sm btn-secondary"
                      href={g.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={g.nombre}
                    >
                      <IconoRegistro tipo={g.tipo} />
                      {' '}{ETIQUETA_REGISTRO[g.tipo]}
                      {pesoLegible(g.size_bytes) && (
                        <span className="text-muted"> · {pesoLegible(g.size_bytes)}</span>
                      )}
                      <IconExternalLink size={11} />
                    </a>
                  ))}
                  {r.google_meet_url && (
                    <a
                      className="btn btn-sm btn-secondary"
                      href={r.google_meet_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <IconExternalLink size={12} /> Meet
                    </a>
                  )}
                </div>
              ) : (
                <p className="text-muted" style={{ fontSize: 13, marginTop: 8 }}>
                  {r.registros_sync_at
                    ? 'No se encontró grabación ni notas en Drive para esta fecha.'
                    : 'Todavía no se buscaron los registros.'}
                </p>
              )}

              {r.notas && (
                <p className="text-muted" style={{ fontSize: 13, marginTop: 8 }}>{r.notas}</p>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
